import {
  ClickAwayListener,
  Grid2 as Grid,
  Grow,
  Paper,
  Popper,
} from "@mui/material";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { SelectInput } from "components/FormControls/Select/Select";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { RefObject } from "react";

import { isEmpty } from "lodash";
import { SelectedFilter } from "types/filters";
import { Conditions } from "../FilterBase/Conditions";
import { Values } from "../FilterBase/Values";
import { FiltersSidebar } from "../FilterSidebar/FiltersSidebar";
import { InlineFilter } from "../InlineFilter/InlineFilter";
import { useStyles } from "../styles";
import { pushFilterWithInitialValue } from "../utils";

interface Props {
  isOpen: boolean;
  anchorRef?: RefObject<HTMLElement>;
  onClose: () => void;
  properties: SelectedFilter[];
  selectedFilters: SelectedFilter[];
  isSidebarOpen: boolean;
  setSidebarOpen: (val: boolean) => void;
  setIsEditingFilters: (val: boolean) => void;
}

export const FiltersList = ({
  isOpen,
  anchorRef,
  onClose,
  properties,
  selectedFilters,
  isSidebarOpen,
  setSidebarOpen,
  setIsEditingFilters,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const size = "small";

  return (
    <FieldArray
      name="filters"
      render={({
        push,
        remove,
        replace,
        form: { values, handleSubmit, setFieldValue, isValid, validateForm },
      }) => {
        const maxVisibleFiltersNumber = 3;
        const isMoreBtnVisible =
          values.filters?.length > maxVisibleFiltersNumber;
        const hiddenFiltersNumber = values.filters?.slice(
          maxVisibleFiltersNumber,
        ).length;

        return (
          <>
            <>
              {!isOpen && values.filters?.length ? (
                <div className={classes.selectedFiltersContainer}>
                  {values.filters
                    ?.slice(0, maxVisibleFiltersNumber)
                    .map((item: SelectedFilter, index: number) => {
                      return (
                        <InlineFilter
                          key={index}
                          filter={item}
                          index={index}
                          setFieldValue={setFieldValue}
                          handleSubmit={handleSubmit}
                          clearFilter={() => {
                            remove(index);
                            return handleSubmit();
                          }}
                          setIsEditingFilters={setIsEditingFilters}
                        />
                      );
                    })}
                  {isMoreBtnVisible && (
                    <OLButton
                      className={classes.moreButton}
                      onClick={() => setSidebarOpen(true)}
                      data-test-id="more-filters-button"
                    >
                      {message(
                        { id: "moreElements" },
                        {
                          count: hiddenFiltersNumber,
                        },
                      )}
                    </OLButton>
                  )}
                </div>
              ) : null}
            </>
            {isSidebarOpen && (
              <FiltersSidebar
                selectedFilters={values.filters}
                isOpen={isSidebarOpen}
                onClose={() => {
                  setSidebarOpen(false);
                  handleSubmit();
                }}
                clearFilters={() => {
                  setFieldValue("filters", []);
                  setSidebarOpen(false);
                  handleSubmit();
                }}
                properties={properties}
                replace={replace}
                handleSubmit={handleSubmit}
              />
            )}

            <Popper
              open={isOpen}
              anchorEl={anchorRef?.current}
              placement="bottom-start"
              transition
              disablePortal
              sx={{ zIndex: 4 }}
              data-test-id="filter-list-container"
            >
              {({ TransitionProps, placement }) => (
                <ClickAwayListener
                  onClickAway={async () => {
                    const isFormValid = isEmpty(await validateForm());
                    if (!isFormValid) return;
                    onClose();
                    handleSubmit();
                  }}
                >
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom-start"
                          ? "left top"
                          : "left bottom",
                    }}
                  >
                    <Paper>
                      <div className={classes.filtersWrapper}>
                        {selectedFilters.map((filter, index) => {
                          const shouldDisplayAndConnector = index > 0;

                          return (
                            <Grid
                              className={classes.filterWrapper}
                              key={`${filter.type}-${index}`}
                            >
                              {shouldDisplayAndConnector && (
                                <span
                                  className={`${classes.grayText} ${classes.textWrapper}`}
                                >
                                  {message({ id: "and" })}
                                </span>
                              )}
                              <Grid size={{ xs: 4 }}>
                                <SelectInput
                                  name={`filters.[${index}].query`}
                                  items={properties.map((singleFilter) => ({
                                    label: singleFilter.label,
                                    id: singleFilter.query,
                                    disabled: !!selectedFilters.find(
                                      (selectedFilter) =>
                                        selectedFilter.query ===
                                        singleFilter.query,
                                    ),
                                  }))}
                                  size={size}
                                  onChange={(_e, newFilterQuery) => {
                                    const newFilter = properties.find(
                                      (filter) =>
                                        filter.query === newFilterQuery,
                                    );
                                    replace(index, newFilter);
                                  }}
                                  isCustomSelect
                                />
                              </Grid>
                              <Grid
                                size={{ xs: 4 }}
                                className={classes.textWrapper}
                              >
                                <Conditions
                                  filter={filter}
                                  index={index}
                                  size={size}
                                />
                              </Grid>
                              <Grid size={{ xs: 4 }}>
                                <Values
                                  filter={filter}
                                  setFieldValue={setFieldValue}
                                  index={index}
                                  size={size}
                                />
                              </Grid>
                              <Grid
                                size={{ xs: 1 }}
                                className={classes.textWrapper}
                              >
                                <XCloseIcon
                                  className={classes.clearIcon}
                                  onClick={() => remove(index)}
                                />
                              </Grid>
                            </Grid>
                          );
                        })}

                        <div className={classes.btnWrapper}>
                          <OLMenuButton
                            data-test-id="add-filter"
                            items={properties.map((filter) => ({
                              label: filter.label,
                              onClick: () =>
                                push(pushFilterWithInitialValue(filter)),
                              isDisabled: !!selectedFilters.find(
                                (selectedFilter) =>
                                  selectedFilter.query === filter.query,
                              ),
                            }))}
                            placeholder={message({ id: "searchAttributes" })}
                            size="small"
                            startIcon={<PlusIcon />}
                            submit={message({ id: "addFilter" })}
                            className={classes.addFilter}
                          />
                          <OLButton
                            data-test-id="save-filter"
                            onClick={async () => {
                              const isFormValid = isEmpty(await validateForm());

                              if (!isFormValid) return;
                              onClose();
                              handleSubmit();
                            }}
                            size="small"
                            variant="contained"
                          >
                            {message({ id: "save" })}
                          </OLButton>
                        </div>
                      </div>
                    </Paper>
                  </Grow>
                </ClickAwayListener>
              )}
            </Popper>
          </>
        );
      }}
    />
  );
};
